/**
 * SyncTemplateInboxDialog - Modal de seleção de inbox para sincronização de templates
 * 
 * Versão: 1.0.0 - 2026-02-28
 * 
 * Exibe lista de inboxes oficiais do tenant para o usuário selecionar
 * qual inbox deseja sincronizar. Previne sincronização acidental de todas
 * as inboxes, protegendo templates de outros números.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Inbox, Layers } from 'lucide-react';

/** Interface da inbox oficial exibida no dialog */
interface OfficialInbox {
  id: string;
  name: string;
  identifier: string;
  provider_slug?: string;
  provider_name?: string;
}

/** Props do componente */
interface SyncTemplateInboxDialogProps {
  /** Controla visibilidade do dialog */
  open: boolean;
  /** Callback para fechar o dialog */
  onOpenChange: (open: boolean) => void;
  /** Lista de inboxes oficiais disponíveis */
  inboxes: OfficialInbox[];
  /** Callback ao confirmar seleção - recebe inbox_id ou 'all' */
  onConfirm: (inboxId: string | 'all') => void;
  /** Indica se a sincronização está em andamento */
  isSyncing?: boolean;
}

/**
 * Dialog para seleção de inbox antes de sincronizar templates.
 * Garante que o usuário escolha conscientemente qual inbox sincronizar,
 * evitando limpeza acidental de templates de outras inboxes.
 */
export function SyncTemplateInboxDialog({
  open,
  onOpenChange,
  inboxes,
  onConfirm,
  isSyncing = false,
}: SyncTemplateInboxDialogProps) {
  // Inbox selecionada pelo usuário
  const [selectedInbox, setSelectedInbox] = useState<string>('');

  /** Handler para confirmar a seleção */
  const handleConfirm = () => {
    if (!selectedInbox) return;
    onConfirm(selectedInbox);
  };

  /** Reseta seleção ao abrir/fechar o dialog */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedInbox('');
    }
    onOpenChange(newOpen);
  };

  /** Retorna label amigável do provider */
  const getProviderLabel = (slug?: string): string => {
    if (!slug) return '';
    const labels: Record<string, string> = {
      'waba': 'WABA',
      'notificame': 'NotificaMe',
      'z-api': 'Z-API',
      'evolution': 'Evolution',
    };
    return labels[slug] || slug;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Sincronizar Templates
          </DialogTitle>
          <DialogDescription>
            Selecione a inbox que deseja sincronizar com o provider. 
            Apenas os templates dessa inbox serão atualizados.
          </DialogDescription>
        </DialogHeader>

        {/* Wrapper scrollável para evitar que a lista empurre o footer para fora da tela */}
        <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
          {/* Lista de inboxes com RadioGroup */}
          <RadioGroup
            value={selectedInbox}
            onValueChange={setSelectedInbox}
            className="gap-3 py-2"
          >
          {/* Opção: Todas as inboxes */}
          <div className="flex items-center space-x-3 rounded-lg border p-3 transition-colors hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="all" id="inbox-all" />
            <Label htmlFor="inbox-all" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Todas as inboxes</span>
                <Badge variant="secondary" className="text-xs">
                  {inboxes.length}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Sincroniza templates de todas as inboxes oficiais
              </p>
            </Label>
          </div>

          {/* Inboxes individuais */}
          {inboxes.map((inbox) => (
            <div
              key={inbox.id}
              className="flex items-center space-x-3 rounded-lg border p-3 transition-colors hover:bg-accent/50 cursor-pointer"
            >
              <RadioGroupItem value={inbox.id} id={`inbox-${inbox.id}`} />
              <Label htmlFor={`inbox-${inbox.id}`} className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{inbox.name}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {inbox.identifier}
                </p>
              </Label>
            </div>
          ))}
          </RadioGroup>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0 px-6 py-4 border-t bg-background">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSyncing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedInbox || isSyncing}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sincronizar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
